import { omitUnchangedLoginEmail, selfProfilePayload } from '@shared/profile-edit';
import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle 
} from "@/components/ui/card";
import { 
  Avatar, 
  AvatarFallback, 
  AvatarImage 
} from "@/components/ui/avatar";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { profileSaveErrorMessage } from '@shared/profile-save-feedback';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getInitials, cn } from "@/lib/utils";
import { UserProfile } from "@shared/types";
import { ROLES } from "@shared/constants";
import {
  getAssignableRolesForActor,
  getRoleLabel as getSharedRoleLabel,
  normalizeRole,
} from "@shared/role-access";
import { useProfilePermissions } from "@/hooks/useProfilePermissions";
import { logger } from "@/lib/logger";
import { useOrganization } from "@/contexts/OrganizationContext";

interface ProfileProps {
  user: UserProfile; // The currently logged in user
}

export default function Profile({ user }: ProfileProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [passwordVisibleUntil, setPasswordVisibleUntil] = useState<number>(0);
  const [passwordTick, setPasswordTick] = useState<number>(Date.now());
  const [, setLocation] = useLocation();
  const params = useParams();
  const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined;
  const urlOrganizationId = search?.get('organizationId') || undefined;
  const employeeId = params.id;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganizationId, setOrganizationId } = useOrganization();
  
  // Use the permission hook
  const permissions = useProfilePermissions(user);
  const actorAssignableRoles = getAssignableRolesForActor(user.role, Boolean(user.isSuperAdmin));

  // Fetch employee data - include organizationId for tenant isolation
  const { data: employee, isLoading, error } = useQuery<UserProfile>({
    queryKey: [`/api/users/${employeeId}`, currentOrganizationId || urlOrganizationId],
    enabled: !!employeeId,
    queryFn: async () => {
      const orgId = currentOrganizationId || urlOrganizationId;
      const orgParam = orgId ? `?organizationId=${encodeURIComponent(orgId)}` : "";
      const resp = await fetch(`/api/users/${employeeId}${orgParam}`, { credentials: "include" });
      if (!resp.ok) {
        throw new Error(`Failed to load profile (${resp.status})`);
      }
      return resp.json();
    }
  });

  const normalizedEmployeeRole = normalizeRole(employee?.role || '');
  const roleOptions = Array.from(
    new Set([
      ...(normalizedEmployeeRole ? [normalizedEmployeeRole] : []),
      ...actorAssignableRoles,
    ]),
  );
  const profileOrganizationId = currentOrganizationId || urlOrganizationId || employee?.organizationId || user.organizationId;

  // Ensure org context matches URL param ASAP to prevent header flipping to default org
  useEffect(() => {
    if (urlOrganizationId && currentOrganizationId !== urlOrganizationId) {
      try { setOrganizationId(urlOrganizationId); } catch {}
    }
  }, [urlOrganizationId, currentOrganizationId, setOrganizationId]);

  // Fetch locations for dropdown selections
  const { data: locations = [] } = useQuery({
    queryKey: [`/api/locations?organizationId=${profileOrganizationId}`],
    enabled: Boolean(profileOrganizationId),
  });

  const { data: organizations = [] } = useQuery<any[]>({
    queryKey: ['organizations-user-access', 'profile'],
    enabled: Boolean(user.isSuperAdmin),
    queryFn: async () => {
      const response = await fetch('/api/organizations/user-access', { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to load organizations (${response.status})`);
      }
      return response.json();
    }
  });
  
  // Fetch all users for scorecard access selection
  const { data: allUsers } = useQuery<UserProfile[]>({
    queryKey: ["/api/users", currentOrganizationId || urlOrganizationId],
    enabled: isEditing,
    queryFn: async () => {
      const orgId = currentOrganizationId || urlOrganizationId;
      const orgParam = orgId ? `?organizationId=${encodeURIComponent(orgId)}` : "";
      const resp = await fetch(`/api/users${orgParam}`, { credentials: "include" });
      if (!resp.ok) throw new Error(`Failed to load users (${resp.status})`);
      return resp.json();
    }
  });

  // Form schema for employee profile editing - INCLUDING ALL MISSING FIELDS
  const formSchema = z.object({
    // Work information
    fullName: z.string().min(1, "Full name is required"),
    email: z.string().email("Invalid email format"),
    newPassword: z.string().optional().or(z.literal("")),
    confirmNewPassword: z.string().optional().or(z.literal("")),
    role: z.string(),
    organizationId: z.string().optional().or(z.literal("")),
    locationId: z.string(),
    locationIds: z.array(z.string()).optional(),
    isDoctor: z.boolean().optional(), // MISSING FIELD ADDED
    
    // Personal information
    personalEmail: z.string().email("Invalid email format").optional().or(z.literal("")),
    personalPhone: z.string().optional().or(z.literal("")),
    homeAddress: z.string().optional().or(z.literal("")),
    birthDate: z.string().optional().or(z.literal("")), // MISSING FIELD ADDED
    maritalStatus: z.string().optional().or(z.literal("")),
    spouseName: z.string().optional().or(z.literal("")),
    numberOfChildren: z.number().min(0).optional(),
    childrenNames: z.string().optional().or(z.literal("")),
    emergencyContact: z.string().optional().or(z.literal("")),
    
    // Professional information
    licenseNumber: z.string().optional().or(z.literal("")), // MISSING FIELD ADDED
    caqhNumber: z.string().optional().or(z.literal("")), // MISSING FIELD ADDED
    npiNumber: z.string().optional().or(z.literal("")), // MISSING FIELD ADDED
    previousWorkExperience: z.string().optional().or(z.literal("")),
    professionalGoals: z.string().optional().or(z.literal("")),
    notes: z.string().optional().or(z.literal("")),
    
    // Access controls - ALL MISSING FIELDS ADDED
    canViewAllScorecards: z.boolean().optional(),
    specificScorecardAccess: z.array(z.string()).optional(),
    headlineAccess: z.object({
      executiveHeadlines: z.boolean(),
      ceoHeadlines: z.boolean(),
      virtualExecutiveHeadlines: z.boolean(),
      teamLeadHeadlines: z.boolean(),
      teamLeadGlobalHeadlines: z.boolean(),
      teamMemberGlobalHeadlines: z.boolean(),
      doctorsHeadlines: z.boolean(),
      billingAndAuditingHeadlines: z.boolean(),
      trainingHeadlines: z.boolean(),
      trainerHeadlines: z.boolean()
    }).optional()
  }).superRefine((values, ctx) => {
    const nextPassword = (values.newPassword || "").trim();
    const confirm = (values.confirmNewPassword || "").trim();

    if (!nextPassword && !confirm) {
      return;
    }

    if (nextPassword.length < 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["newPassword"],
        message: "New password must be at least 10 characters",
      });
    }

    if (!confirm) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["confirmNewPassword"],
        message: "Please confirm the new password",
      });
    } else if (nextPassword !== confirm) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["confirmNewPassword"],
        message: "Passwords do not match",
      });
    }
  });

  // Initialize form with employee data
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      newPassword: "",
      confirmNewPassword: "",
      role: "",
      organizationId: "",
      locationId: "",
      locationIds: [],
      isDoctor: false,
      personalEmail: "",
      personalPhone: "",
      homeAddress: "",
      birthDate: "",
      maritalStatus: "",
      spouseName: "",
      numberOfChildren: 0,
      childrenNames: "",
      emergencyContact: "",
      licenseNumber: "",
      caqhNumber: "",
      npiNumber: "",
      previousWorkExperience: "",
      professionalGoals: "",
      notes: "",
      canViewAllScorecards: false,
      specificScorecardAccess: [],
      headlineAccess: {
        executiveHeadlines: false,
        ceoHeadlines: false,
        virtualExecutiveHeadlines: false,
        teamLeadHeadlines: false,
        teamLeadGlobalHeadlines: false,
        teamMemberGlobalHeadlines: false,
        doctorsHeadlines: false,
        billingAndAuditingHeadlines: false,
        trainingHeadlines: false,
        trainerHeadlines: false
      }
    },
  });

  const watchedRole = form.watch("role");
  const isPasswordVisible = passwordVisibleUntil > passwordTick;
  const passwordSecondsLeft = isPasswordVisible
    ? Math.max(1, Math.ceil((passwordVisibleUntil - passwordTick) / 1000))
    : 0;

  const revealPasswordFor20Seconds = () => {
    setPasswordVisibleUntil(Date.now() + 20_000);
    setPasswordTick(Date.now());
  };

  useEffect(() => {
    if (!isPasswordVisible) return;
    const timerId = window.setInterval(() => setPasswordTick(Date.now()), 250);
    return () => window.clearInterval(timerId);
  }, [isPasswordVisible]);

  useEffect(() => {
    if (!isEditing) {
      setPasswordVisibleUntil(0);
    }
  }, [isEditing]);

  useEffect(() => {
    const normalized = normalizeRole(watchedRole);
    if (normalized === ROLES.TEAM_LEAD_DOCTOR && !form.getValues("isDoctor")) {
      form.setValue("isDoctor", true, { shouldValidate: true });
    } else if (normalized === ROLES.TEAM_LEAD_OPTICAL && form.getValues("isDoctor")) {
      form.setValue("isDoctor", false, { shouldValidate: true });
    }
  }, [watchedRole, form]);

  // Update form values when employee data is loaded
  useEffect(() => {
    if (employee) {
      logger.info('Loading employee data into form', { employeeId: employee.id });
      
      form.reset({
        fullName: employee.fullName,
        email: employee.email,
        newPassword: "",
        confirmNewPassword: "",
        role: employee.role,
        organizationId: employee.organizationId || "",
        locationId: employee.locationId || "",
        locationIds: employee.locationIds || [],
        isDoctor: employee.isDoctor || false,
        personalEmail: employee.personalEmail || "",
        personalPhone: employee.personalPhone || "",
        homeAddress: employee.homeAddress || "",
        birthDate: employee.birthDate ? new Date(employee.birthDate).toISOString().split('T')[0] : "",
        maritalStatus: employee.maritalStatus || "",
        spouseName: employee.spouseName || "",
        numberOfChildren: employee.numberOfChildren || 0,
        childrenNames: employee.childrenNames || "",
        emergencyContact: employee.emergencyContact || "",
        licenseNumber: employee.licenseNumber || "",
        caqhNumber: employee.caqhNumber || "",
        npiNumber: employee.npiNumber || "",
        previousWorkExperience: employee.previousWorkExperience || "",
        professionalGoals: employee.professionalGoals || "",
        notes: employee.notes || "",
        canViewAllScorecards: employee.canViewAllScorecards || false,
        specificScorecardAccess: employee.specificScorecardAccess || [],
        headlineAccess: {
          executiveHeadlines: false,
          ceoHeadlines: false,
          virtualExecutiveHeadlines: false,
          teamLeadHeadlines: false,
          teamLeadGlobalHeadlines: false,
          teamMemberGlobalHeadlines: false,
          doctorsHeadlines: false,
          billingAndAuditingHeadlines: false,
          trainingHeadlines: false,
          trainerHeadlines: false,
          ...(employee.headlineAccess || {}),
        }
      });
    }
  }, [employee, form]);

  // Mutation for updating employee profile
  const updateMutation = useMutation({
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      logger.info('Updating employee profile', { employeeId });

      // Convert birthDate string to Date if provided
      const { newPassword, confirmNewPassword, ...rest } = values;
      let dataToSend: Record<string, unknown> = {
        ...rest,
        birthDate: values.birthDate ? new Date(values.birthDate) : null,
      };
      const normalizedRole = normalizeRole(String(values.role || ''));
      if (normalizedRole) {
        dataToSend.role = normalizedRole;
        if (normalizedRole === ROLES.TEAM_LEAD_DOCTOR) {
          dataToSend.isDoctor = true;
        }
        if (normalizedRole === ROLES.TEAM_LEAD_OPTICAL) {
          dataToSend.isDoctor = false;
        }
      }
      if (!user.isSuperAdmin) {
        delete dataToSend.organizationId;
      }
      if (newPassword?.trim()) {
        dataToSend.password = newPassword.trim();
      }

      if (!user.isSuperAdmin) dataToSend = selfProfilePayload(dataToSend);
      dataToSend = omitUnchangedLoginEmail(dataToSend, employee?.email);

      // Ensure CSRF cookie exists before mutation and capture token from response
      let csrf: string | null = null;
      try {
        const csrfResp = await fetch('/api/auth/csrf', { method: 'GET', credentials: 'include', redirect: 'error' });
        const csrfJson = await csrfResp.json().catch(() => ({} as any));
        if (csrfJson?.csrfToken) {
          csrf = csrfJson.csrfToken;
          (window as any).__CSRF_TOKEN = csrf;
        }
        // Fallback to cookie if present
        if (!csrf) {
          const m = document.cookie.match(/(?:^|; )boltos_csrf_token=([^;]+)/);
          csrf = m ? decodeURIComponent(m[1]) : null;
        }
      } catch {}

      const orgId = currentOrganizationId || urlOrganizationId;
      const orgParam = orgId ? `?organizationId=${encodeURIComponent(orgId)}` : "";
      const resp = await fetch(`/api/users/${employeeId}${orgParam}`, {
        method: 'PATCH',
        redirect: 'error',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf as string } : {})
        } as Record<string, string>,
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });
      const json = await resp.json().catch(() => ({} as any));
      if (!resp.ok) {
        throw new Error(json?.error || json?.message || `HTTP ${resp.status}`);
      }
      return json;
    },
    onSuccess: () => {
      toast({
        title: "Profile Updated",
        description: "The employee profile has been successfully updated."
      });
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: [`/api/users/${employeeId}`, currentOrganizationId || urlOrganizationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/users", currentOrganizationId || urlOrganizationId] });
    },
    onError: (error) => {
      logger.error('Profile update failed');
      toast({
        title: "Update Failed",
        description: profileSaveErrorMessage(error),
        variant: "destructive"
      });
    }
  });

  // Generate role label from enum value
  const getRoleLabel = (role: string) => {
    const sharedLabel = getSharedRoleLabel(role);
    if (sharedLabel) return sharedLabel;

    const roleMap: Record<string, string> = {
      [ROLES.CEO]: 'CEO',
      [ROLES.EXECUTIVE_TEAM]: 'Executive',
      [ROLES.NEXT_GEN_EXECUTIVE_TEAM]: 'NextGen Executive',
      [ROLES.TRAINING_LEVEL]: 'Training Level',
      [ROLES.TEAM_LEAD]: 'Team Lead',
      [ROLES.TEAM_MEMBER]: 'Team Member',
      [ROLES.VIRTUAL_EXECUTIVE]: 'Virtual Executive',
      [ROLES.EXECUTIVE_VIRTUAL_TEAM]: 'Executive Virtual Team',
      [ROLES.VIRTUAL_TEAM_MEMBER]: 'Virtual Team Member',
      [ROLES.BILLING_AUDITING]: 'Billing-Auditing',
      [ROLES.BOLT_TASKFORCE]: 'Bolt Eye TaskForce',
      [ROLES.TRAINER]: 'Trainer'
    };
    
    return roleMap[role] || role;
  };

  // Handle form submission
  const onSubmit = (values: z.infer<typeof formSchema>) => {
    updateMutation.mutate(values);
  };

  // Handle cancelling edit mode
  const handleCancel = () => {
    setIsEditing(false);
    if (employee) {
      form.reset({
        fullName: employee.fullName,
        email: employee.email,
        newPassword: "",
        confirmNewPassword: "",
        role: employee.role,
        organizationId: employee.organizationId || "",
        locationId: employee.locationId || "",
        locationIds: employee.locationIds || [],
        isDoctor: employee.isDoctor || false,
        personalEmail: employee.personalEmail || "",
        personalPhone: employee.personalPhone || "",
        homeAddress: employee.homeAddress || "",
        birthDate: employee.birthDate ? new Date(employee.birthDate).toISOString().split('T')[0] : "",
        maritalStatus: employee.maritalStatus || "",
        spouseName: employee.spouseName || "",
        numberOfChildren: employee.numberOfChildren || 0,
        childrenNames: employee.childrenNames || "",
        emergencyContact: employee.emergencyContact || "",
        licenseNumber: employee.licenseNumber || "",
        caqhNumber: employee.caqhNumber || "",
        npiNumber: employee.npiNumber || "",
        previousWorkExperience: employee.previousWorkExperience || "",
        professionalGoals: employee.professionalGoals || "",
        notes: employee.notes || "",
        canViewAllScorecards: employee.canViewAllScorecards || false,
        specificScorecardAccess: employee.specificScorecardAccess || [],
        headlineAccess: {
          executiveHeadlines: false,
          ceoHeadlines: false,
          virtualExecutiveHeadlines: false,
          teamLeadHeadlines: false,
          teamLeadGlobalHeadlines: false,
          teamMemberGlobalHeadlines: false,
          doctorsHeadlines: false,
          billingAndAuditingHeadlines: false,
          trainingHeadlines: false,
          trainerHeadlines: false,
          ...(employee.headlineAccess || {}),
        }
      });
    }
  };

  // Handle back button
  const handleBack = () => {
    setLocation('/team');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-xl font-medium mb-2">Error Loading Profile</h2>
        <p className="text-neutral-500 mb-4">Failed to load employee profile. Please try again.</p>
        <Button onClick={handleBack}>Back to Team</Button>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-xl font-medium mb-2">Employee Not Found</h2>
        <p className="text-neutral-500 mb-4">The requested employee profile could not be found.</p>
        <Button onClick={handleBack}>Back to Team</Button>
      </div>
    );
  }

  // Get the location name for the employee's primary location
  const getLocationName = (locationId: string) => {
    if (!locations || !Array.isArray(locations)) return "Unknown";
    const location = locations.find((loc: any) => loc.id === locationId);
    return location ? location.name : "Unknown";
  };
  
  // Check if user can edit this profile at all
  const canEditProfile = permissions.canEditUser(employee);

  return (
    <div className="container px-4 py-8 mx-auto max-w-6xl">
      {/* Header with back button */}
      <div className="flex items-center mb-6">
        <Button 
          variant="ghost" 
          onClick={handleBack}
          className="mr-4"
        >
          <span className="mdi mdi-arrow-left mr-2"></span>
          Back
        </Button>
        <h1 className="text-2xl font-medium">Employee Profile</h1>
      </div>

      {/* Profile Header Card */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center">
            <Avatar className="h-24 w-24 mb-4 md:mb-0 md:mr-6">
              {employee.avatarUrl ? (
                <AvatarImage src={employee.avatarUrl} alt={employee.fullName} />
              ) : (
                <AvatarFallback className="text-xl bg-primary text-white">
                  {getInitials(employee.fullName)}
                </AvatarFallback>
              )}
            </Avatar>
            
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                <div>
                  <h2 className="text-2xl font-bold">{employee.fullName}</h2>
                  <p className="text-neutral-500">{employee.email}</p>
                </div>
                <div className="flex items-center gap-2 mt-2 md:mt-0">
                  <Badge className="w-fit">{getRoleLabel(employee.role)}</Badge>
                  {employee.isDoctor && (
                    <Badge variant="secondary" className="w-fit">
                      <span className="mdi mdi-stethoscope mr-1"></span>
                      Doctor
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <p className="text-sm text-neutral-500">Primary Location</p>
                  <p>{getLocationName(employee.locationId || '')}</p>
                </div>
                {employee.birthDate && (
                  <div>
                    <p className="text-sm text-neutral-500">Date of Birth</p>
                    <p>{new Date(employee.birthDate).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Edit Button - Only shown for users with permission */}
            {canEditProfile && (
              <div className="mt-4 md:mt-0 md:ml-4">
                {isEditing ? (
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      onClick={handleCancel}
                      disabled={updateMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="default" 
                      type="button"
                      data-testid="profile-save"
                      onClick={() => {
                        if (!isEditing) setIsEditing(true);
                        const safeSubmit = form.handleSubmit(onSubmit);
                        safeSubmit();
                      }}
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                    {null}
                  </div>
                ) : (
                  <Button 
                    onClick={() => setIsEditing(true)}
                  >
                    <span className="mdi mdi-pencil mr-2"></span>
                    Edit Profile
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Profile Content with Tabs - NOW INCLUDING ACCESS CONTROLS */}
      <Tabs defaultValue="personal" className="mb-6">
        <TabsList className="mb-6">
          <TabsTrigger value="personal">Personal Information</TabsTrigger>
          <TabsTrigger value="professional">Professional Information</TabsTrigger>
          <TabsTrigger value="locations">Location Assignment</TabsTrigger>
          <TabsTrigger value="access">
            Access Controls
            {!permissions.canEditTab('access', employee) && (
              <span className="ml-1 text-gray-400" title="View only">🔒</span>
            )}
          </TabsTrigger>
        </TabsList>

        {isEditing ? (
          // Edit Form Mode
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <TabsContent value="personal" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Personal Information</CardTitle>
                    <CardDescription>
                      Edit personal details and contact information
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="fullName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name</FormLabel>
                            <FormControl>
                              <Input data-testid="profile-full-name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Work Email</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="newPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Reset Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  type={isPasswordVisible ? "text" : "password"}
                                  autoComplete="new-password"
                                  {...field}
                                  className="pr-20"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-2 text-xs"
                                  onClick={revealPasswordFor20Seconds}
                                >
                                  <span className={`mdi ${isPasswordVisible ? "mdi-eye-off-outline" : "mdi-eye-outline"} mr-1`} />
                                  {isPasswordVisible ? `${passwordSecondsLeft}s` : "Show"}
                                </Button>
                              </div>
                            </FormControl>
                            <FormDescription>
                              Leave blank to keep the current password.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="confirmNewPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Confirm New Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  type={isPasswordVisible ? "text" : "password"}
                                  autoComplete="new-password"
                                  {...field}
                                  className="pr-20"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-2 text-xs"
                                  onClick={revealPasswordFor20Seconds}
                                >
                                  <span className={`mdi ${isPasswordVisible ? "mdi-eye-off-outline" : "mdi-eye-outline"} mr-1`} />
                                  {isPasswordVisible ? `${passwordSecondsLeft}s` : "Show"}
                                </Button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="personalEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Personal Email</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="personalPhone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Personal Phone</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="homeAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Home Address</FormLabel>
                          <FormControl>
                            <Textarea {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="birthDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth</FormLabel>
                          <FormControl>
                            <Input {...field} type="date" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="maritalStatus"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Marital Status</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select marital status" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="single">Single</SelectItem>
                                <SelectItem value="married">Married</SelectItem>
                                <SelectItem value="divorced">Divorced</SelectItem>
                                <SelectItem value="widowed">Widowed</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="spouseName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Spouse Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="numberOfChildren"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Number of Children</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                min="0"
                                {...field}
                                onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="childrenNames"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Children Names</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription>
                              Separate names with commas
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="emergencyContact"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Emergency Contact</FormLabel>
                          <FormControl>
                            <Textarea {...field} />
                          </FormControl>
                          <FormDescription>
                            Include name, relationship, and contact information
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="professional" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Professional Information</CardTitle>
                    <CardDescription>
                      Edit work-related details and professional background
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Role</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              value={field.value}
                              disabled={!permissions.canEditField('role', employee).canEdit}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {roleOptions.map((roleValue) => (
                                  <SelectItem key={roleValue} value={roleValue}>
                                    {getRoleLabel(roleValue)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="isDoctor"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-2">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>
                                Is Doctor
                              </FormLabel>
                              <FormDescription>
                                Check if this employee is a medical doctor
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Medical Credentials - shown only if isDoctor is true */}
                    {form.watch("isDoctor") && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <FormField
                          control={form.control}
                          name="licenseNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>License Number</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Medical license #" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="caqhNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>CAQH Number</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="CAQH provider #" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="npiNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>NPI Number</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="National Provider ID" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    <FormField
                      control={form.control}
                      name="previousWorkExperience"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Previous Work Experience</FormLabel>
                          <FormControl>
                            <Textarea {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="professionalGoals"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Professional Goals</FormLabel>
                          <FormControl>
                            <Textarea {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes</FormLabel>
                          <FormControl>
                            <Textarea {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="locations" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Location Assignment</CardTitle>
                    <CardDescription>
                      Edit primary location and additional assigned locations
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <FormField
                      control={form.control}
                      name="locationId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary Location</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select primary location" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Array.isArray(locations) && locations.map((location: any) => (
                                <SelectItem key={location.id} value={location.id}>
                                  {location.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div>
                      <FormLabel className="mb-2 block">Additional Assigned Locations</FormLabel>
                      <FormDescription className="mb-4">
                        An employee can be assigned to up to 10 locations
                      </FormDescription>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Array.isArray(locations) && locations.map((location: any) => {
                          // Skip if it's the primary location
                          if (location.id === form.getValues("locationId")) {
                            return null;
                          }
                          const watchedLocationIds = form.watch("locationIds") || [];
                          
                          return (
                            <div key={location.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`location-${location.id}`}
                                checked={watchedLocationIds.includes(location.id)}
                                onCheckedChange={(checked) => {
                                  const currentLocations = form.getValues("locationIds") || [];
                                  
                                  if (checked) {
                                    // Prevent duplicates
                                    if (!currentLocations.includes(location.id) && currentLocations.length < 10) {
                                      form.setValue("locationIds", [...currentLocations, location.id], { shouldValidate: true });
                                    } else {
                                      toast({
                                        title: "Maximum Locations Reached",
                                        description: "An employee can be assigned to a maximum of 10 locations",
                                        variant: "destructive"
                                      });
                                    }
                                  } else {
                                    form.setValue(
                                      "locationIds", 
                                      currentLocations.filter(id => id !== location.id),
                                      { shouldValidate: true }
                                    );
                                  }
                                }}
                              />
                              <Label htmlFor={`location-${location.id}`}>{location.name}</Label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="access" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Access Controls</CardTitle>
                    <CardDescription>
                      Configure scorecard and headline access permissions
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {user.isSuperAdmin && (
                      <FormField
                        control={form.control}
                        name="organizationId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Organization</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value || ''}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select organization" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {organizations.map((organization: { id: string; name: string }) => (
                                  <SelectItem key={organization.id} value={organization.id}>
                                    {organization.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Super admins can move this user between organizations.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {/* Scorecard Access Section */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-lg">Scorecard Access</h3>
                      
                      <FormField
                        control={form.control}
                        name="canViewAllScorecards"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={!permissions.canEditField('canViewAllScorecards', employee).canEdit}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>
                                Can View All Scorecards
                                {!permissions.canEditField('canViewAllScorecards', employee).canEdit && (
                                  <span className="ml-1 text-gray-400">🔒</span>
                                )}
                              </FormLabel>
                              <FormDescription>
                                Allow this user to view all employee scorecards regardless of location
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                      
                      {!form.watch("canViewAllScorecards") && (
                        <div>
                          <FormLabel className="mb-2 block">
                            Specific Scorecard Access
                            {!permissions.canEditField('specificScorecardAccess', employee).canEdit && (
                              <span className="ml-1 text-gray-400">🔒</span>
                            )}
                          </FormLabel>
                          <FormDescription className="mb-4">
                            Select which employee scorecards this user can view
                          </FormDescription>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-64 overflow-y-auto border rounded-md p-4">
                            {allUsers?.map((user) => {
                              // Don't show the current employee in the list
                              if (user.id === employee.id) return null;
                              
                              const watchedScorecardAccess = form.watch("specificScorecardAccess") || [];
                              
                              return (
                                <div key={user.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`scorecard-${user.id}`}
                                    checked={watchedScorecardAccess.includes(user.id)}
                                    disabled={!permissions.canEditField('specificScorecardAccess', employee).canEdit}
                                    onCheckedChange={(checked) => {
                                      const currentAccess = form.getValues("specificScorecardAccess") || [];
                                      
                                      if (checked) {
                                        form.setValue("specificScorecardAccess", [...currentAccess, user.id], { shouldValidate: true });
                                      } else {
                                        form.setValue(
                                          "specificScorecardAccess", 
                                          currentAccess.filter(id => id !== user.id),
                                          { shouldValidate: true }
                                        );
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`scorecard-${user.id}`} className="text-sm">
                                    {user.fullName} ({getRoleLabel(user.role)})
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <Separator />
                    
                    {/* Headline Access Section */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-lg">Role-Based Headline Access</h3>
                      <FormDescription>
                        Configure which role-specific headline types this user can view.
                        Note: Location-based headlines are automatically granted based on assigned locations.
                      </FormDescription>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="headlineAccess.executiveHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  Executive Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="headlineAccess.ceoHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  CEO Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="headlineAccess.virtualExecutiveHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  Virtual Executive Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="headlineAccess.teamLeadHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  Team Lead Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="headlineAccess.billingAndAuditingHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  Billing &amp; Auditing Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="headlineAccess.trainingHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  Training Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="headlineAccess.trainerHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  Trainer Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="headlineAccess.teamLeadGlobalHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  Team Lead Global Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="headlineAccess.teamMemberGlobalHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  Team Member Global Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="headlineAccess.doctorsHeadlines"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={!permissions.canEditField('headlineAccess', employee).canEdit}
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel>
                                  Doctors' Headlines
                                  {!permissions.canEditField('headlineAccess', employee).canEdit && (
                                    <span className="ml-1 text-gray-400">🔒</span>
                                  )}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </form>
          </Form>
        ) : (
          // View Mode
          <>
            <TabsContent value="personal" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Personal Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-medium text-lg mb-4">Contact Details</h3>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm text-neutral-500">Work Email</Label>
                          <p>{employee.email || <span className="text-neutral-400">—</span>}</p>
                        </div>

                        <div>
                          <Label className="text-sm text-neutral-500">Password</Label>
                          <p className="font-mono tracking-widest">••••••••••••</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm text-neutral-500">Personal Email</Label>
                          <p>{employee.personalEmail || <span className="text-neutral-400">—</span>}</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm text-neutral-500">Personal Phone</Label>
                          <p>{employee.personalPhone || <span className="text-neutral-400">—</span>}</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm text-neutral-500">Home Address</Label>
                          <p className="whitespace-pre-wrap">{employee.homeAddress || <span className="text-neutral-400">—</span>}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-4">Family Information</h3>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-sm text-neutral-500">Date of Birth</Label>
                          <p>{employee.birthDate ? new Date(employee.birthDate).toLocaleDateString() : <span className="text-neutral-400">—</span>}</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm text-neutral-500">Marital Status</Label>
                          <p className="capitalize">{employee.maritalStatus || <span className="text-neutral-400">—</span>}</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm text-neutral-500">Spouse Name</Label>
                          <p>{employee.spouseName || <span className="text-neutral-400">—</span>}</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm text-neutral-500">Number of Children</Label>
                          <p>{employee.numberOfChildren !== undefined && employee.numberOfChildren !== null ? employee.numberOfChildren : <span className="text-neutral-400">—</span>}</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm text-neutral-500">Children Names</Label>
                          <p>{employee.childrenNames || <span className="text-neutral-400">—</span>}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h3 className="font-medium text-lg mb-2">Emergency Contact</h3>
                    <p className="whitespace-pre-wrap">{employee.emergencyContact || <span className="text-neutral-400">Include name, relationship, and contact information</span>}</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="professional" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Professional Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-medium text-lg mb-4">Edit work-related details and professional background</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-sm text-neutral-500">Role</Label>
                        <p>{getRoleLabel(employee.role) || <span className="text-neutral-400">—</span>}</p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox checked={employee.isDoctor || false} disabled />
                        <Label className="text-sm">
                          Is Doctor
                          <span className="ml-2 text-xs text-neutral-500">
                            Check if this employee is a medical doctor
                          </span>
                        </Label>
                      </div>
                    </div>
                    
                    {employee.isDoctor && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <Label className="text-sm text-neutral-500">License Number</Label>
                          <p>{employee.licenseNumber || <span className="text-neutral-400">—</span>}</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm text-neutral-500">CAQH Number</Label>
                          <p>{employee.caqhNumber || <span className="text-neutral-400">—</span>}</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm text-neutral-500">NPI Number</Label>
                          <p>{employee.npiNumber || <span className="text-neutral-400">—</span>}</p>
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">Previous Work Experience</h3>
                      <p className="whitespace-pre-wrap">{employee.previousWorkExperience || <span className="text-neutral-400">—</span>}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">Professional Goals</h3>
                      <p className="whitespace-pre-wrap">{employee.professionalGoals || <span className="text-neutral-400">—</span>}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">Notes</h3>
                      <p className="whitespace-pre-wrap">{employee.notes || <span className="text-neutral-400">—</span>}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="locations" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Location Assignment</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-medium text-lg mb-4">Edit primary location and additional assigned locations</h3>
                    </div>
                    
                    <div>
                      <Label className="text-sm text-neutral-500 mb-2 block">Primary Location</Label>
                      <p>{employee.locationId ? getLocationName(employee.locationId) : <span className="text-neutral-400">Select primary location</span>}</p>
                    </div>
                    
                    <div>
                      <Label className="text-sm text-neutral-500 mb-2 block">Additional Assigned Locations</Label>
                      <p className="text-sm text-neutral-500 mb-2">No employee can be assigned to up to 10 locations</p>
                      {employee.locationIds && employee.locationIds.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {employee.locationIds.map((locId) => (
                            <Badge key={locId} variant="outline">
                              {getLocationName(locId)}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-neutral-400">No additional locations assigned</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="access" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Access Controls</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-medium text-lg mb-4">Configure scorecard and headline access permissions</h3>
                    </div>
                    
                    {/* Scorecard Access Section */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-lg">Scorecard Access</h3>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox checked={employee.canViewAllScorecards || false} disabled />
                        <Label className="text-sm">
                          Can View All Scorecards
                          <span className="ml-2 text-xs text-neutral-500">
                            Allow this employee to view all employee scorecards regardless of location
                          </span>
                        </Label>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-neutral-500 mb-2 block">Specific Scorecard Access</Label>
                        <p className="text-sm text-neutral-500 mb-2">Select which employee scorecards this user can view</p>
                        {employee.specificScorecardAccess && employee.specificScorecardAccess.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {employee.specificScorecardAccess.map((userId) => {
                              const user = allUsers?.find(u => u.id === userId);
                              return user ? (
                                <Badge key={userId} variant="outline">
                                  {user.fullName}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        ) : (
                          <p className="text-neutral-400">No specific scorecard access granted</p>
                        )}
                      </div>
                    </div>
                    
                    <Separator />
                    
                    {/* Headline Access Section */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-lg">Headline Access</h3>
                      
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> This employee automatically sees headlines for their assigned locations:
                          <span className="font-medium"> {getLocationName(employee.locationId || '')}</span>
                          {employee.locationIds && employee.locationIds.length > 0 && (
                            <span className="font-medium">, {employee.locationIds.map(id => getLocationName(id)).join(', ')}</span>
                          )}
                        </p>
                      </div>
                      
                      <h4 className="font-medium">Role-Based Headlines</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {employee.headlineAccess && (
                          <>
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.executiveHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.executiveHeadlines ? "✓" : "✗"}
                              </span>
                              <span>Executive Headlines</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.ceoHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.ceoHeadlines ? "✓" : "✗"}
                              </span>
                              <span>CEO Headlines</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.virtualExecutiveHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.virtualExecutiveHeadlines ? "✓" : "✗"}
                              </span>
                              <span>Virtual Executive Headlines</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.teamLeadHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.teamLeadHeadlines ? "✓" : "✗"}
                              </span>
                              <span>Team Lead Headlines</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.billingAndAuditingHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.billingAndAuditingHeadlines ? "✓" : "✗"}
                              </span>
                              <span>Billing &amp; Auditing Headlines</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.trainingHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.trainingHeadlines ? "✓" : "✗"}
                              </span>
                              <span>Training Headlines</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.trainerHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.trainerHeadlines ? "✓" : "✗"}
                              </span>
                              <span>Trainer Headlines</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.teamLeadGlobalHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.teamLeadGlobalHeadlines ? "✓" : "✗"}
                              </span>
                              <span>Team Lead Global Headlines</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.teamMemberGlobalHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.teamMemberGlobalHeadlines ? "✓" : "✗"}
                              </span>
                              <span>Team Member Global Headlines</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={cn("text-lg", employee.headlineAccess.doctorsHeadlines ? "text-green-600" : "text-gray-400")}>
                                {employee.headlineAccess.doctorsHeadlines ? "✓" : "✗"}
                              </span>
                              <span>Doctors' Headlines</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
